import { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { TrendingUp, MousePointerClick, Award, LayoutGrid } from 'lucide-react';
import { subDays, format } from 'date-fns';

type TimeRange = 7 | 30 | 90;

interface ClickRow {
  id: string;
  partner_slug: string;
  brand_name: string;
  category_slug: string;
  created_at: string;
  source_page: string;
}

function useAffiliateClicks(days: TimeRange) {
  return useQuery({
    queryKey: ['affiliate-clicks', days],
    queryFn: async () => {
      const since = subDays(new Date(), days).toISOString();
      const { data, error } = await (supabase as any)
        .from('affiliate_clicks')
        .select('id, partner_slug, brand_name, category_slug, created_at, source_page')
        .gte('created_at', since)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ClickRow[];
    },
  });
}

function StatCard({ label, value, icon: Icon, sub }: { label: string; value: string | number; icon: React.ElementType; sub?: string }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex items-start gap-4">
      <div className="p-2.5 rounded-xl bg-primary/10">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
        {sub && <p className="text-xs text-primary mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function AdminAffiliateAnalytics() {
  const [range, setRange] = useState<TimeRange>(30);
  const { data: clicks = [], isLoading } = useAffiliateClicks(range);

  // Aggregate by partner
  const partnerStats = Object.values(
    clicks.reduce<Record<string, { partner: string; brand: string; category: string; clicks: number }>>((acc, c) => {
      if (!acc[c.partner_slug]) {
        acc[c.partner_slug] = { partner: c.partner_slug, brand: c.brand_name, category: c.category_slug, clicks: 0 };
      }
      acc[c.partner_slug].clicks += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.clicks - a.clicks);

  // Aggregate by category
  const categoryStats = Object.values(
    clicks.reduce<Record<string, { category: string; clicks: number }>>((acc, c) => {
      if (!acc[c.category_slug]) {
        acc[c.category_slug] = { category: c.category_slug.replace(/-/g, ' '), clicks: 0 };
      }
      acc[c.category_slug].clicks += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.clicks - a.clicks);

  const topPartner = partnerStats[0]?.brand ?? '—';
  const topCategory = categoryStats[0]?.category ?? '—';

  const CHART_COLORS = ['hsl(var(--primary))', 'hsl(var(--primary) / 0.7)', 'hsl(var(--primary) / 0.5)', 'hsl(var(--primary) / 0.35)', 'hsl(var(--primary) / 0.2)'];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 container max-w-6xl mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Affiliate Analytics</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Partner click performance across the Gift Store</p>
            </div>

            {/* Time range filter */}
            <div className="flex gap-2">
              {([7, 30, 90] as TimeRange[]).map((d) => (
                <Button
                  key={d}
                  variant={range === d ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRange(d)}
                >
                  {d}d
                </Button>
              ))}
            </div>
          </div>

          {/* Overview cards */}
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              <StatCard label={`Total Clicks (${range}d)`} value={clicks.length} icon={MousePointerClick} />
              <StatCard label="Top Partner" value={topPartner} icon={Award} />
              <StatCard label="Top Category" value={topCategory} icon={LayoutGrid} />
              <StatCard label="Unique Partners" value={partnerStats.length} icon={TrendingUp} />
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-8 mb-10">
            {/* Category bar chart */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <h2 className="text-base font-semibold text-foreground mb-4">Clicks by Category</h2>
              {isLoading ? (
                <Skeleton className="h-48 w-full rounded-xl" />
              ) : categoryStats.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">No data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={categoryStats} layout="vertical" margin={{ left: 16, right: 16 }}>
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={130} />
                    <Tooltip
                      contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                    />
                    <Bar dataKey="clicks" radius={[0, 6, 6, 0]}>
                      {categoryStats.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Daily trend placeholder */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <h2 className="text-base font-semibold text-foreground mb-4">Partner Leaderboard</h2>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 rounded-lg" />)}
                </div>
              ) : partnerStats.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">No data yet.</p>
              ) : (
                <div className="space-y-2">
                  {partnerStats.slice(0, 8).map((p, i) => (
                    <div key={p.partner} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
                      <span className="text-xs font-bold text-muted-foreground w-5">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{p.brand}</p>
                        <p className="text-xs text-muted-foreground capitalize">{p.category.replace(/-/g, ' ')}</p>
                      </div>
                      <span className="text-sm font-bold text-primary">{p.clicks}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Full clicks table */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-base font-semibold text-foreground">Recent Clicks</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Partner</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Category</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Source</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                          <td colSpan={4} className="px-6 py-3"><Skeleton className="h-5 w-full rounded" /></td>
                        </tr>
                      ))
                    : clicks.slice(0, 50).map((c) => (
                        <tr key={c.id} className="border-b border-border/40 hover:bg-muted/20 transition-colors">
                          <td className="px-6 py-3 font-medium text-foreground">{c.brand_name}</td>
                          <td className="px-6 py-3 text-muted-foreground capitalize">{c.category_slug.replace(/-/g, ' ')}</td>
                          <td className="px-6 py-3 text-muted-foreground">{c.source_page}</td>
                          <td className="px-6 py-3 text-muted-foreground text-xs">{format(new Date(c.created_at), 'MMM d, h:mm a')}</td>
                        </tr>
                      ))}
                  {!isLoading && clicks.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-10 text-center text-muted-foreground text-sm">
                        No clicks recorded in this time range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
